import { computed, watch } from 'vue';
import { findRegion, genSearcherByKey } from '../utils/utils';
import { dummyIndex, globalRegionIndex, searcherUndefined, searhcerCompareKey } from '../utils/consts';
import type { Props, Region, SearcherIndexed } from '../types';
import { useSelectSearcher } from './selectSearcher';
import { useSelectRegion } from './selectRegion';
import { useCompare } from './compare';

/**
 * Создание и управления рективными переменными компонента
 *
 * @param props - входные props компонента
 */
export const useSelectorRegion = (props: Props) => {
	const searcherByKey = computed(() => {
		return genSearcherByKey(props.forFrequency, props.autoRegion, props.searchers);
	});

	const activeSearcherIndexed = computed(() => {
		return searcherByKey.value.get(selectSearcher.searcherKey.value) || searcherUndefined;
	});

	/**
	 * Все индексы регионов из searcherByKey
	 *
	 * Активность регионов определяется в searcherByKey
	 */
	const allRegionsIndexes = computed(() => {
		const regionsIndexes = new Set<number>();

		searcherByKey.value.forEach((searcher) => {
			searcher.regionByIndex.forEach((region) => {
				if (region.index === globalRegionIndex) return;
				if (region.index === dummyIndex) return;

				regionsIndexes.add(region.index);
			});
		});

		return regionsIndexes;
	});

	const selectSearcher = useSelectSearcher(props, searcherByKey);
	const selectRegion = useSelectRegion(props, activeSearcherIndexed);
	const compare = useCompare(props, searcherByKey, allRegionsIndexes);

	// контроль за внешним изменением списка ПС
	watch(searcherByKey, () => {
		// возможные значения для сравнения регионов/пс
		if (props.onlySearcher) {
			compare.regionsIndexes.value = Array.from(searcherByKey.value.keys());
		} else {
			compare.regionsIndexes.value = compare.regionsIndexes.value.filter(regionIndex => {
				return allRegionsIndexes.value.has(regionIndex);
			});
		}

		if (selectSearcher.searcherKey.value === searhcerCompareKey) return;

		let newSearcherKey = searcherByKey.value.keys().next().value;

		searcherByKey.value.forEach((searcher) => {
			// определить выбранную ПС
			if (props.onlySearcher && searcher.key === selectSearcher.searcherKey.value) {
				newSearcherKey = selectSearcher.searcherKey.value;
				return;
			}

			if (
				selectRegion.regionIndex.value &&
				searcher.regionByIndex?.has(selectRegion.regionIndex.value)
			) {
				newSearcherKey = searcher.key;
			}

			// выбрать первую ПС с выбранным регионом
			if (!props.onlySearcher) {
				let regionsNewSearcher: SearcherIndexed['regionByIndex'] | undefined;

				if (newSearcherKey !== undefined) {
					regionsNewSearcher = searcherByKey.value.get(newSearcherKey)?.regionByIndex;
				}

				const regionsCurrentSearcher = searcherByKey.value.get(searcher.key)?.regionByIndex;
				if (
					regionsNewSearcher?.has(dummyIndex) &&
					!regionsCurrentSearcher?.has(dummyIndex)
				) {
					newSearcherKey = searcher.key;
				}
			}
		});

		// if (props.addCompare && !selectSearcher.optionBySearcherKey.value.has(dummyIndex)) {
		// 	if (!selectRegion.regionIndex.value && compare.regionsIndexes.value?.length) newSearcherKey = searhcerCompareKey;
		// }

		// if (props.onlySearcher && selectRegion.regionIndex.value !== dummyIndex) newSearcherKey = selectRegion.regionIndex.value;

		if (newSearcherKey !== undefined) {
			selectSearcher.searcherKey.value = newSearcherKey;
		}

		if (
			selectRegion.regionIndex.value !== undefined &&
			!(activeSearcherIndexed.value?.regionByIndex)?.has(selectRegion.regionIndex.value)
		) {
			selectRegion.regionIndex.value = activeSearcherIndexed.value?.regions?.keys().next().value as number;
		}
	}, { immediate: true });

	const getSearcherKey = () => {
		if (selectSearcher.searcherKey.value === searhcerCompareKey || selectSearcher.searcherKey.value === dummyIndex) return;

		return selectSearcher.searcherKey.value;
	};

	const getRegionIndex = () => {
		if (props.onlySearcher) return;

		if (selectRegion.regionIndex.value === dummyIndex) return;

		let res: Region['index'] | undefined = selectRegion.regionIndex.value;

		// в качестве ключа используется region.key
		if (props.forFrequency) {
			const regionKey = selectRegion.regionIndex.value;
			const region = findRegion(props.forFrequency, { searcher_key: getSearcherKey(), key: regionKey } as Region, props.searchers);

			res = region?.index;
		}

		return res;
	};

	/**
	 * Получить выбранную ПС
	 */
	const getSearcher = () => {
		const searcherKey = getSearcherKey();
		if (searcherKey === undefined) return;

		return searcherByKey.value.get(searcherKey);
	};

	/**
	 * Получить выбранный регион
	 */
	const getRegion = () => {
		const regionIndex = getRegionIndex();
		if (regionIndex === undefined) return;

		return getSearcher()?.regionByIndex?.get(regionIndex);
	};

	return {
		selectSearcher,
		selectRegion,
		compare,

		searcherByKey,
		allRegionsIndexes,

		getSearcher,
		getRegion,
	};
};
